import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Pagination from '@mui/material/Pagination';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import { checkPermission } from '../../utils/checkPermissions';

export default function Solicitors() {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [searchUsername, setSearchUsername] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [statusOptions, setStatusOptions] = useState([
    { value: 'PENDING', label: 'Pending' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'BLOCKED', label: 'Blocked' }
  ]);

  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;
  const canManageSolicitors = checkPermission(userPermissions, 'settings', 'manage_solicitors');
  const debounceRef = useRef();

  const fetchSolicitors = async (searchNameVal, searchUsernameVal, searchEmailVal, searchStatusVal, pageVal = page) => {
    setLoading(true);
    try {
      const payload = {
        page: pageVal,
        limit: 10,
        name: searchNameVal,
        username: searchUsernameVal,
        email: searchEmailVal,
        status: searchStatusVal || undefined,
        role: 'SOLICITOR',
      };

      const response = await postRequest('private/userList', payload, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setUserList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
        // Update status options if returned from API
        if (response.statusOptions) {
          setStatusOptions(response.statusOptions);
        }
      } else {
        setUserList([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      setUserList([]);
      setTotalPages(1);
      setTotalDocs(0);
    }
    setLoading(false);
  };

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchSolicitors(searchName, searchUsername, searchEmail, searchStatus, page);
    // eslint-disable-next-line
  }, [page, authCtx.token]);

  // Debounced search on name, username, email or status change
  useEffect(() => {
    setPage(1); // Reset to first page on search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSolicitors(searchName, searchUsername, searchEmail, searchStatus, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchName, searchUsername, searchEmail, searchStatus]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const response = await postRequest(
        `private/updateUserStatus/${userId}`,
        { status: newStatus },
        authCtx.token
      );

      if (response?.status === 200) {
        Swal.fire('Success!', 'Solicitor status updated successfully.', 'success');
        // Refresh the solicitor list
        fetchSolicitors(searchName, searchUsername, searchEmail, searchStatus, page);
      } else {
        Swal.fire('Failed!', response?.message || 'Failed to update status.', 'error');
      }
    } catch (err) {
      Swal.fire('Error!', err?.message || 'Something went wrong.', 'error');
    }
  };

  const handleDelete = async (userId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this solicitor!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#bb0000',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await postRequest(`private/deleteUser/${userId}`, {}, authCtx.token);
        if (response?.status === 200) {
          Swal.fire('Deleted!', 'Solicitor has been deleted.', 'success');

          const payload = {
            page,
            limit: 10,
            name: searchName,
            username: searchUsername,
            email: searchEmail,
            status: searchStatus || undefined,
            role: 'SOLICITOR',
          };
          const listResponse = await postRequest('private/userList', payload, authCtx.token);
          const docs = listResponse?.data?.docs || [];
          const totalPagesAfterDelete = listResponse?.data?.totalPages || 1;

          if (docs.length === 0 && page > 1) {
            setPage((prev) => prev - 1);
          } else {
            setUserList(docs);
            setTotalPages(totalPagesAfterDelete);
          }
        } else {
          Swal.fire('Failed!', response?.message || 'Something went wrong.', 'error');
        }
      } catch (err) {
        Swal.fire('Error!', err?.message || 'Something went wrong.', 'error');
      }
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Solicitors', path: '' },
        ]}
      />
      <br></br>
      <div className='page-header'>
        <h1 className='mt-0'>Solicitors</h1>
        {canManageSolicitors && (
          <div className='header-right-btn'>
            <Stack spacing={2} direction="row">
              <Button variant="outlined" className='red-outline-btn text-capitalize'>
                <NavLink to="/solicitors/create" className="red-link-text">
                  <PersonAddOutlinedIcon /> &nbsp; Add New Solicitor
                </NavLink>
              </Button>
            </Stack>
          </div>
        )}
      </div>
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={searchName}
                onChange={e => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={searchUsername}
                onChange={e => {
                  setSearchUsername(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter email"
                value={searchEmail}
                onChange={e => {
                  setSearchEmail(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={searchStatus}
                onChange={e => {
                  setSearchStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Status</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
        </Form>
      </div>

      <div>
        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Username</th>
              <th>User Group</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : userList.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>
                  No solicitors found.
                </td>
              </tr>
            ) : (
              userList.map((user) => (
                <tr key={user._id}>
                  <th>{user?.forename} {user?.surname}</th>
                  <td>{user?.email}</td>
                  <td>{user?.username}</td>
                  <td>{user?.userGroupName || '-'}</td>
                  <td>
                    {canManageSolicitors ? (
                      <Form.Select
                        size="sm"
                        value={user?.status || 'PENDING'}
                        onChange={(e) => handleStatusChange(user._id, e.target.value)}
                        className={`status-select ${
                          user?.status === 'ACTIVE' ? 'text-success' :
                          user?.status === 'PENDING' ? 'text-warning' :
                          user?.status === 'BLOCKED' ? 'text-danger' :
                          'text-secondary'
                        }`}
                        style={{
                          fontWeight: 'bold',
                          maxWidth: '140px'
                        }}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <span
                        className={`badge ${
                          user?.status === 'ACTIVE' ? 'bg-success' :
                          user?.status === 'PENDING' ? 'bg-warning text-dark' :
                          user?.status === 'BLOCKED' ? 'bg-danger' :
                          'bg-secondary'
                        }`}
                      >
                        {user?.status || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="View"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/solicitors/view/${user._id}`} className="red-link-text">
                        <VisibilityOutlinedIcon />
                      </NavLink>
                    </span>
                    {canManageSolicitors && (
                      <>
                        <span
                          className='red-link-text cursor-pointer w-18'
                          title="Edit"
                          style={{ marginRight: 8 }}
                        >
                          <NavLink to={`/solicitors/edit/${user._id}`} className="red-link-text">
                            <EditOutlinedIcon />
                          </NavLink>
                        </span>
                        <span
                          className='red-link-text cursor-pointer w-18'
                          title="Delete"
                          onClick={() => handleDelete(user._id)}
                        >
                          <DeleteOutlineOutlinedIcon />
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {/* Pagination */}
        {userList.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
              <span className="text-muted small">
                Showing {((page - 1) * 10) + 1}-{Math.min(page * 10, totalDocs)} of {totalDocs} solicitors
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
